import { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { addEdge, applyEdgeChanges, applyNodeChanges, Background, ReactFlow, Controls } from 'reactflow';
import { useTranslation } from 'react-i18next';
import 'reactflow/dist/style.css';

const Graphic = ({ nodeTypes, tenant, result, areas, processes, systems }) => {
  const { t } = useTranslation(['mapping', 'common']);

  // State to control which nodes are expanded
  const [expandedNodes, setExpandedNodes] = useState(new Set([`${tenant?.slug}-company`]));

  const toggleNodeExpansion = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  const initialNodes = useMemo(() => {
    const nodes = [];

    // Central company node
    nodes.push({
      id: `${tenant.slug}-company`,
      sourcePosition: 'bottom',
      targetPosition: 'top',
      type: 'company',
      className: 'company_node',
      data: {
        name: tenant.name,
        slug: tenant.slug,
        result: result,
        isExpanded: expandedNodes.has(`${tenant.slug}-company`),
        onToggleExpand: () => toggleNodeExpansion(`${tenant.slug}-company`),
        hasChildren: areas && areas.length > 0,
      },
      position: { x: 600, y: 50 },
    });

    if (expandedNodes.has(`${tenant.slug}-company`)) {
      areas?.forEach((area, areaIndex) => {
        const xPosition = 100 + (areaIndex * 600);
        const areaNodeId = `area-${area._id}`;

        nodes.push({
          id: areaNodeId,
          type: 'section',
          className: 'area_node',
          data: {
            area: t('mapping:nodeTypes.area'),
            name: area.name,
            description: area.description,
            user: area.user,
            criticity: area.criticity,
            processCount: area.processes?.length || 0,
            systemCount: area.systems?.length || 0,
            assetCount: area.assets?.length || 0,
            isExpanded: expandedNodes.has(areaNodeId),
            onToggleExpand: () => toggleNodeExpansion(areaNodeId),
            hasChildren: (area.processes?.length || 0) + (area.systems?.length || 0) + (area.assets?.length || 0) > 0,
          },
          position: { x: xPosition, y: 250 },
        });

        if (expandedNodes.has(areaNodeId)) {
          area.processes?.forEach((process, processIndex) => {
            nodes.push({
              id: `process-${process._id}`,
              type: 'section',
              className: 'process_node',
              data: {
                area: t('mapping:nodeTypes.process'),
                name: process.name,
                description: process.description,
                user: process.user,
                criticity: process.criticity,
                dpia: process.dpia,
                lia: process.lia,
                dataMapping: process.dataMapping?.length || 0,
                isExpanded: false,
                hasChildren: false,
              },
              position: { x: xPosition - 150 + (processIndex * 250), y: 450 },
            });
          });
        }

        if (expandedNodes.has(areaNodeId)) {
          area.systems?.forEach((system, systemIndex) => {
            nodes.push({
              id: `system-${system._id}`,
              type: 'section',
              className: 'system_node',
              data: {
                area: t('mapping:nodeTypes.system'),
                name: system.name,
                description: system.description,
                user: system.user,
                criticity: system.criticity,
                saas: system.saas,
                developedInHouse: system.developedInHouse,
                isExpanded: false,
                hasChildren: false,
              },
              position: { x: xPosition + (systemIndex * 280), y: 650 },
            });
          });
        }

        if (expandedNodes.has(areaNodeId)) {
          area.assets?.forEach((asset, assetIndex) => {
            nodes.push({
              id: `asset-${asset._id}`,
              type: 'section',
              className: 'asset_node',
              data: {
                area: t('mapping:nodeTypes.asset'),
                name: asset.name,
                description: asset.description,
                user: asset.user,
                criticity: asset.criticity,
                type: asset.type,
                pii: asset.pii,
                location: asset.location,
                last: true,
                isExpanded: false,
                hasChildren: false,
              },
              position: { x: xPosition + 100 + (assetIndex * 260), y: 850 },
            });
          });
        }
      });
    }

    return nodes;
  }, [tenant, result, areas, expandedNodes, toggleNodeExpansion, t]);

  const initialEdges = useMemo(() => {
    const edges = [];

    if (expandedNodes.has(`${tenant.slug}-company`)) {
      areas?.forEach((area) => {
        edges.push({
          id: `company-to-area-${area._id}`,
          source: `${tenant.slug}-company`,
          target: `area-${area._id}`,
          type: 'default',
          style: { stroke: '#666', strokeWidth: 2 },
        });

        if (expandedNodes.has(`area-${area._id}`)) {
          area.processes?.forEach((process) => {
            edges.push({
              id: `area-to-process-${area._id}-${process._id}`,
              source: `area-${area._id}`,
              target: `process-${process._id}`,
              type: 'default',
              style: { stroke: '#e74c3c', strokeWidth: 1.5 },
            });
          });
        }

        if (expandedNodes.has(`area-${area._id}`)) {
          area.systems?.forEach((system) => {
            edges.push({
              id: `area-to-system-${area._id}-${system._id}`,
              source: `area-${area._id}`,
              target: `system-${system._id}`,
              type: 'default',
              style: { stroke: '#3498db', strokeWidth: 1.5 },
            });
          });
        }

        if (expandedNodes.has(`area-${area._id}`)) {
          area.assets?.forEach((asset) => {
            edges.push({
              id: `area-to-asset-${area._id}-${asset._id}`,
              source: `area-${area._id}`,
              target: `asset-${asset._id}`,
              type: 'default',
              style: { stroke: '#2ecc71', strokeWidth: 1.5 },
            });
          });
        }
      });
    }

    processes?.forEach((process) => {
      process.systems?.forEach((system) => {
        edges.push({
          id: `process-to-system-${process._id}-${system._id}`,
          source: `process-${process._id}`,
          target: `system-${system._id}`,
          type: 'default',
          style: { stroke: '#9b59b6', strokeWidth: 1.5 },
        });
      });

      process.assets?.forEach((asset) => {
        edges.push({
          id: `process-to-asset-${process._id}-${asset._id}`,
          source: `process-${process._id}`,
          target: `asset-${asset._id}`,
          type: 'default',
          style: { stroke: '#f39c12', strokeWidth: 1.5 },
        });
      });
    });

    systems?.forEach((system) => {
      system.assets?.forEach((asset) => {
        edges.push({
          id: `system-to-asset-${system._id}-${asset._id}`,
          source: `system-${system._id}`,
          target: `asset-${asset._id}`,
          type: 'default',
          style: { stroke: '#1abc9c', strokeWidth: 1.5 },
        });
      });
    });

    return edges;
  }, [tenant, areas, processes, systems, expandedNodes]);

  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);
  const reactFlowInstance = useRef(null);

  useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes]);

  useEffect(() => {
    setEdges(initialEdges);
  }, [initialEdges]);

  useEffect(() => {
    if (initialNodes.length > 0 && reactFlowInstance.current) {
      setTimeout(() => {
        if (reactFlowInstance.current) {
          reactFlowInstance.current.fitView({
            padding: 30,
            includeHiddenNodes: false,
            minZoom: 1.0,
            maxZoom: 2.5
          });
        }
      }, 300);
    }
  }, [initialNodes, initialEdges]);

  const onInit = useCallback((instance) => {
    reactFlowInstance.current = instance;
    setTimeout(() => {
      instance.fitView({
        padding: 30,
        includeHiddenNodes: false,
        minZoom: 1.0,
        maxZoom: 2.5
      });
    }, 100);
  }, []);

  const onNodesChange = useCallback((changes) => setNodes((nds) => applyNodeChanges(changes, nds)), [setNodes]);
  const onEdgesChange = useCallback((changes) => setEdges((eds) => applyEdgeChanges(changes, eds)), [setEdges]);
  const onConnect = useCallback((connection) => setEdges((eds) => addEdge(connection, eds)), [setEdges]);

  return (
    <div style={{ width: '100%', height: '700px' }}>
      <ReactFlow
        nodeTypes={nodeTypes}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={onInit}
        fitView
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
        panOnDrag={true}
        zoomOnScroll={true}
        zoomOnPinch={true}
        panOnScroll={false}
        minZoom={0.5}
        maxZoom={2}
        defaultViewport={{ x: 0, y: 0, zoom: 1.4 }}
        fitViewOptions={{
          padding: 50,
          includeHiddenNodes: false,
          minZoom: 1.0,
          maxZoom: 3.0
        }}
      >
        <Background
          variant="lines"
          gap={25}
          size={1.5}
          color="#e0e0e0"
        />
        <Controls
          showInteractive={false}
          position="bottom-right"
        />
      </ReactFlow>
    </div>
  );
};

export default Graphic;
